import React from 'react';
import { Card, Box, Typography, IconButton, Tooltip } from '@mui/material';
import {
  PhoneCallback as PhoneCallbackIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import CallStatusBadge from './CallStatusBadge';

interface CallSummaryCardProps {
  call: {
    call_id: string;
    customer_name?: string;
    customer_phone?: string;
    start_time?: string;
    duration?: number;
    issue_category?: string;
    issue_details?: string;
    urgency_level?: string;
    resolved?: boolean;
    callback_requested?: boolean;
    callback_status?: string;
    human_transfer_requested?: boolean;
    escalation_requested?: boolean;
    under_30_seconds?: boolean;
    status?: string;
  };
  onViewDetails?: (callId: string) => void;
}

const CallSummaryCard: React.FC<CallSummaryCardProps> = ({ call, onViewDetails }) => {
  const formatPhoneNumber = (phone?: string) => {
    if (!phone) return 'N/A';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    } else if (cleaned.length === 10) {
      return `+1 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const formatDuration = (duration?: number) => {
    if (!duration || duration <= 0) return 'N/A';
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatTime = (timestamp?: string) => {
    if (!timestamp) return 'N/A';
    try {
      return new Date(timestamp).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const handleClick = () => {
    if (onViewDetails) {
      onViewDetails(call.call_id);
    }
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #e0e0e0',
        transition: 'all 0.3s ease-in-out',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          transform: 'translateY(-2px)',
          borderColor: '#1976d2'
        }
      }}
      onClick={handleClick}
    >
      <Box sx={{ p: { xs: 1.5, sm: 2 }, flex: 1, minHeight: { xs: '180px', sm: '200px', md: 'auto' } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#333333',
                fontSize: { xs: '0.9rem', sm: '1rem' },
                mb: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {call.customer_name || 'Unknown Caller'}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#666666',
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {formatPhoneNumber(call.customer_phone)}
            </Typography>
          </Box>
          <Box sx={{ alignSelf: { xs: 'flex-start', sm: 'flex-end' }, flexShrink: 0 }}>
            <CallStatusBadge
              status={call.status}
              resolved={call.resolved}
              urgencyLevel={call.urgency_level}
              callbackRequested={call.callback_requested}
              callbackStatus={call.callback_status}
              humanTransferRequested={call.human_transfer_requested}
              escalationRequested={call.escalation_requested}
              under30Seconds={call.under_30_seconds}
            />
          </Box>
        </Box>

        {/* Details */}
        <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: { xs: 0.5, sm: 1 }, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0.25, sm: 0 } }}>
            <Typography variant="body2" sx={{ color: '#666666', fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: { xs: 500, sm: 400 } }}>
              Time:
            </Typography>
            <Typography variant="body2" sx={{ color: '#333333', fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 500 }}>
              {formatTime(call.start_time)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: { xs: 0.5, sm: 1 }, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0.25, sm: 0 } }}>
            <Typography variant="body2" sx={{ color: '#666666', fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: { xs: 500, sm: 400 } }}>
              Duration:
            </Typography>
            <Typography variant="body2" sx={{ color: '#333333', fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 500 }}>
              {formatDuration(call.duration)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: { xs: 0.5, sm: 1 }, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0.25, sm: 0 } }}>
            <Typography variant="body2" sx={{ color: '#666666', fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: { xs: 500, sm: 400 } }}>
              Category:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#333333',
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {call.issue_category || 'N/A'}
            </Typography>
          </Box>
        </Box>

        {/* Issue Preview */}
        {call.issue_details && (
          <Box sx={{ backgroundColor: '#f8f9fa', p: { xs: 1, sm: 1.5 }, borderRadius: '6px', mb: { xs: 1.5, sm: 2 } }}>
            <Typography
              variant="body2"
              sx={{
                color: '#666666',
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                lineHeight: 1.4,
                display: '-webkit-box',
                WebkitLineClamp: { xs: 1, sm: 2 },
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              {call.issue_details}
            </Typography>
          </Box>
        )}

        {/* Footer */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0.5, sm: 0 } }}>
          <Typography variant="caption" sx={{ color: '#666666', fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>
            Click to view details
          </Typography>
          <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 } }}>
            {call.callback_requested && (
              <PhoneCallbackIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: '#ff9800' }} />
            )}
            {call.urgency_level?.toLowerCase() === 'high' && (
              <WarningIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: '#f44336' }} />
            )}
            {call.resolved && (
              <CheckCircleIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: '#4caf50' }} />
            )}
            {onViewDetails && (
              <Tooltip title="View Details">
                <IconButton size="small" sx={{ p: 0.5 }}>
                  <VisibilityIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: '#1976d2' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

export default CallSummaryCard;

